/**
 * AdvancedAnalysisButton - Context-aware button for advanced analysis
 * Shows "Run" or "View" based on analysis existence status
 */

import React from "react";
import {Button} from "reactstrap";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import {AnalysisStatus} from "../../types/advancedAnalysis";
import {ClipLoader} from "react-spinners";
import {useUser} from "../../contexts/AuthContext";
import ReactGA from "react-ga4";
import {GoogleAnalyticsEventCategories} from "../../constants";

export interface AdvancedAnalysisButtonProps {
  status: AnalysisStatus;
  onRunAnalysis: () => void;
  onViewAnalysis: () => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

const AdvancedAnalysisButton: React.FC<AdvancedAnalysisButtonProps> = ({
  status,
  onRunAnalysis,
  onViewAnalysis,
  disabled = false,
  size = "sm"
}) => {
  const isChecking = status === "checking";
  const exists = status === "exists";
  const isDisabled = disabled || isChecking;
  const {user} = useUser();

  const userId: string = user.identityId as string;

  const handleClick = () => {
    if (exists) {
      ReactGA.event({
        category: GoogleAnalyticsEventCategories.ANALYSIS,
        action: "view advanced analysis",
        label: userId
      });
      onViewAnalysis();
    } else {
      ReactGA.event({
        category: GoogleAnalyticsEventCategories.ANALYSIS,
        action: "run advanced analysis",
        label: userId
      });
      onRunAnalysis();
    }
  };

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <Button
          color="primary"
          data-color={color}
          onClick={handleClick}
          disabled={isDisabled}
          className="advanced-analysis-button mr-2"
          size={size}
        >
          {isChecking ? (
            <>
              <ClipLoader color="#ffffff" size={16} loading={true} />
              <span className="ml-2">Checking...</span>
            </>
          ) : exists ? (
            <>
              <i className="fa fa-chart-line mr-1"></i>
              View Advanced Analysis
            </>
          ) : (
            <>
              <i className="fa fa-play mr-1"></i>
              Run Advanced Analysis
            </>
          )}
        </Button>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default AdvancedAnalysisButton;
